import moment from 'moment';
import { IResponse, IToastConfig } from '../../interfaces/common';
import { DATE_PATTERN, MIMEMAP, NOTIFY_TYPE } from '../constants/enums';
import { ListMessage } from '../message';
import { notification, Upload, UploadFile } from 'antd';
import { AxiosResponse } from 'axios';
import { PRODUCT } from '../constants/itemScreen';
import { RULE } from '../constants/regex';

/**
 * Render message ID
 * @param messageID String
 * @param arrs Array
 * @returns
 */
export const renderMessageID = (messageID: string, arrs?: string[]) => {
  let message = ListMessage.message[messageID as keyof typeof ListMessage.message];

  if (!message) {
    return 'メッセージが見つかりません！';
  }

  if (arrs) {
    for (let i = 0; i < arrs.length; i++) {
      message = message.replace('{' + i + '}', arrs[i]);
    }
  }
  return message;
};

/**
 * Open notification dialog
 * @param config
 * @param type
 */
export const OpenToast = (config: IToastConfig, type: string) => {
  notification.config({
    top: 80,
  });

  const notificationConfig = {
    message: config.message,
    description: config.description,
  };

  switch (type) {
    case NOTIFY_TYPE.SUCCESS:
      notification.success(notificationConfig);
      break;
    case NOTIFY_TYPE.ERROR:
      notification.error(notificationConfig);
      break;
    case NOTIFY_TYPE.INFO:
      notification.info(notificationConfig);
      break;
    case NOTIFY_TYPE.WARNING:
      notification.warning(notificationConfig);
      break;
    default:
      notification.open(notificationConfig);
      break;
  }
};

/**
 * Handle convert date time api to view screen
 * @param (date: string, format: string)
 * @returns
 */
export const formatDate = (date: string, format: string) => {
  return moment.utc(date).local().format(format);
};

/**
 * Compare whether two objects are different or not
 * @param obj1
 * @param obj2
 */
export const compareObjects = (obj1: Record<string, string | number>, obj2: Record<string, string | number>) => {
  // The data may not be of the same type. Convert data to the same string type for comparison.
  function convertToString(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString();
    }
    if (value && (typeof value === 'object' || Array.isArray(value))) {
      return JSON.stringify(value);
    }
    return value;
  }

  // Compare two objects
  for (const key of Object.keys({ ...obj1, ...obj2 })) {
    const value1 = obj1[key];
    const value2 = obj2[key];

    const value1Str = convertToString(value1);
    const value2Str = convertToString(value2);

    if (value1Str !== value2Str) {
      return false;
    }
  }
  // Returns true if the two objects are the same and vice versa
  return true;
};

/**
 * Handle transform response to IResponse
 * @param response AxiosResponse
 * @returns IResponse
 */
export const transformResponseToIResponse = (response: AxiosResponse): IResponse => {
  let message = response?.data?.message || response?.data?.error?.message;

  // Replace the message with a custom message
  if (message === 'Invalid credentials') {
    message = 'アカウントが無効です。社内管理者に確認してください。';
  }

  return {
    status: response?.status ?? 503,
    data: response?.data?.data || response?.data || null,
    message: message,
    isError: response?.status >= 400,
    details: response?.data?.error?.details || null,
    meta: response?.data?.meta || undefined,
  };
};

/**
 * Handle create before upload
 * @param accept string
 * @returns
 */
export const createBeforeUpload = (accept: string) => {
  return (file: UploadFile) => {
    const allowedExtensions = accept.split(',').map(ext => ext.trim()); // Separate file types in accept
    const allowedMimeTypes = allowedExtensions.map(ext => MIMEMAP[ext] || ext); // Get MIME type from map

    const isAllowedType = file.type ? allowedMimeTypes.includes(file.type) : false; // Check the MIME type of the file

    if (!isAllowedType) {
      const extensionsMessage = allowedExtensions.join(', ');
      OpenToast(
        {
          message: `${extensionsMessage}ファイルのみアップロードできます！`,
        },
        NOTIFY_TYPE.ERROR
      );
    }

    return isAllowedType || Upload.LIST_IGNORE;
  };
};

/**
 * Input integers only
 * @param e React.ChangeEvent<HTMLInputElement>
 * @param onChange  (value: string) => void
 */
export const handleInputIntegersOnly = (e: React.ChangeEvent<HTMLInputElement>) => {
  return (e.target.value = e.target.value.replace(RULE.NUMBER, ''));
};

/**
 * Handle input number or float
 * @param e React.ChangeEvent<HTMLInputElement>
 */
export const handleInputNumberOrFloat = (e: React.ChangeEvent<HTMLInputElement>) => {
  return (e.target.value = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1'));
};

/**
 * Handle validate input type
 * @param e React.ChangeEvent<HTMLInputElement>
 * @param type 'text' | 'number' | 'date' | 'dropdown'
 */
export const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'text' | 'number' | 'date' | 'dropdown' | 'float') => {
  const { value: inputValue } = e.target;
  if (type === 'float') {
    e.target.value = inputValue.replace(/[^0-9.]/g, '');

    if ((inputValue.match(/\./g) || []).length > 1) {
      e.target.value = inputValue.replace(/\.(?=.*\.)/g, '');
    }

    const [integer, decimal] = inputValue.split('.');
    if (decimal && decimal.length > 2) {
      e.target.value = `${integer}.${decimal.slice(0, 2)}`;
    }
  }
  if (type === 'number') {
    e.target.value = inputValue.replace(/[^0-9]/g, '');
  }
};

/**
 * @param value string
 * @param onChange
 */
export const handleTextInputBlur = (value: string, onChange: (value: string) => void) => {
  let valueTemp = value;
  // If there are any non-alphabet characters, remove them
  valueTemp = valueTemp.replace(/[^A-Za-z]/g, '');
  onChange(valueTemp);
};

// Define the mapping for converting keys to Japanese labels
const HeaderProductMaster = [
  PRODUCT.NITTOKU_CODE,
  PRODUCT.PACKAGING_ITEM_CODE,
  PRODUCT.STOCK_NO,
  PRODUCT.NUMBER_PRODUCT,
  PRODUCT.NUMBER,
  PRODUCT.NAME_NIHON_SHIKO,
  PRODUCT.NAME_NITTOKU,
  PRODUCT.PACKAGING_CODE,
  PRODUCT.TERMINAL,
  PRODUCT.SIZE_CLASSIFICATION,
  PRODUCT.SIZE_NITTO,
  'デザインＮｏ',
  PRODUCT.PRODUCT_PRICE,
  PRODUCT.WORK_PROCESS_PRICE,
  PRODUCT.SHELF_NO,
  PRODUCT.NOTE_1,
  PRODUCT.NOTE_2,
  PRODUCT.NOTE_3,
  PRODUCT.SEMI_FINISHED_QUANTITY,
  PRODUCT.REAL_INVENTORY,
  PRODUCT.SCHEDULED_DELIVERY_REMAINING,
];

// Define the desired order of fields
const FIELD_ORDER = [
  'nittoku_code',
  'packaging_item_code',
  'stock_no',
  'number_product',
  'number',
  'name_nihon_shiko',
  'name_nittoku',
  'packaging_code',
  'terminal',
  'size_class',
  'size_nitto',
  'design_no',
  'product_price',
  'work_price_process',
  'shelf_no',
  'note_one',
  'note_two',
  'note_three',
  'semi_finished_quantity',
  'real_inventory',
  'scheduled_delivery_remaining',
];

/**
 * Exports the specified data as a CSV file.
 * @param {Array<Object>} data - Array of objects to be exported as CSV (e.g., [{id: 1, name: 'Product 1'}, ...])
 * @param {string} [filename='data.csv'] - The name of the CSV file to be exported (default is 'data.csv')
 * @returns {void} - No return value, but a CSV file is downloaded.
 */
/* eslint-disable */
export const exportProductMasterCSV = (data: any[], filename = '製品マスタ.csv') => {
  const date = new Date();
  const fullYear = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  let fileName = `製品マスタ_${fullYear}${month}${day}${hours}${minutes}.csv`;

  // Check if data exists. If not, display an error message and stop.
  if (!data || !data.length) {
    console.error('エクスポートするデータがありません'); // Error message in Japanese: "No data to export"
    return;
  }

  // Generate the CSV content by creating a header row, then adding data rows.
  const csvContent = [
    HeaderProductMaster.join(','), // Header row (e.g., "id,name,price")
    ...data.map(row =>
      // Convert each field to CSV format and join with commas
      FIELD_ORDER.map(fieldName => JSON.stringify(row[fieldName])).join(',')
    ),
  ].join('\r\n'); // Join rows with newline characters

  // Create a Blob from the CSV content
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a link element for downloading the file
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob); // Generate a temporary URL from the Blob

  link.href = url; // Set the link URL to the Blob URL
  link.setAttribute('download', fileName); // Set the download attribute with the filename

  // Trigger the download by clicking the link
  link.click();

  // Release the URL after use to free up memory
  URL.revokeObjectURL(url);
};

export const formatDateInvalid = (value: moment.MomentInput) =>
  value && moment(value).isValid() ? moment(value).format(DATE_PATTERN.YYYY_MM_DD) : '-';

/**
 * Chunk an array into smaller arrays of a specified size
 * @param array
 * @param size
 * @returns
 */
export const chunkArray = <T>(array: T[], size: number): T[][] => {
  if (!Array.isArray(array)) {
    throw new Error('Input must be an array.');
  }

  if (size <= 0) {
    throw new Error('Chunk size must be greater than 0.');
  }

  const chunked: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunked.push(array.slice(i, i + size));
  }
  return chunked;
};

/**
 * Using Web Workers for Parallel Data Processing with Chunking
 */
export const processWithWorker = async <T, R>(data: T[], processor: (item: T) => R, chunkSize: number = 300): Promise<R[]> => {
  const chunkedData = chunkArray(data, chunkSize); // Divide data into small chunks

  // Process all chunks in parallel for better performance
  const chunkResults = await Promise.all(chunkedData.map(chunk => processChunkWithWorker(chunk, processor)));

  return chunkResults.flat() as R[];
};

/**
 * Helper function to process a single chunk with Web Worker
 */
const processChunkWithWorker = <T, R>(dataChunk: T[], processor: (item: T) => R): Promise<R[]> => {
  return new Promise((resolve, reject) => {
    // Create a new Worker instance
    const worker = new Worker(new URL('./worker.ts', import.meta.url));

    worker.postMessage({
      data: dataChunk,
      processorString: processor.toString(),
    });

    // Handle the message event when the Worker finishes processing
    worker.onmessage = e => {
      if (e.data?.error) {
        reject(new Error(e.data.error));
      } else {
        resolve(e.data);
      }
      worker.terminate();
    };

    // Handle the error event if the Worker encounters an error
    worker.onerror = e => {
      reject(e.message);
      worker.terminate();
    };
  });
};

/**
 * Using Web Workers for Parallel Data Processing with Chunking
 */
export const processWithWorkerProduct = async <T, R>(data: T[], processor: (item: T) => R, chunkSize: number = 300): Promise<R[]> => {
  const chunkedData = chunkArray(data, chunkSize); // Divide data into small chunks

  // Process all chunks in parallel for better performance
  const chunkResults = await Promise.all(chunkedData.map(chunk => processChunkWithWorkerProduct(chunk, processor)));

  return chunkResults.flat() as R[];
};

/**
 * Helper function to process a single chunk with Web Worker
 */
const processChunkWithWorkerProduct = <T, R>(dataChunk: T[], processor: (item: T) => R): Promise<R[]> => {
  return new Promise((resolve, reject) => {
    // Create a new Worker instance
    const worker = new Worker(new URL('./workerProduct.ts', import.meta.url));

    worker.postMessage({
      data: dataChunk,
    });

    // Handle the message event when the Worker finishes processing
    worker.onmessage = e => {
      if (e.data?.error) {
        reject(new Error(e.data.error));
      } else {
        resolve(e.data);
      }
      worker.terminate();
    };

    // Handle the error event if the Worker encounters an error
    worker.onerror = e => {
      reject(e.message);
      worker.terminate();
    };
  });
};
